import * as React from 'react';
import { MdBusiness, MdClose, MdHealing, MdLocationOn, MdSearch, MdVisibility } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import DataGrid from '../../Components/Table/DataGrid';
import { APPRoutes } from '../../Utils/Route';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { toast } from 'react-toastify';
import PageTitle from '../../Components/Common/PageTitle';
import { getValueBasedCareOrganizations } from '../../Services/ValueBasedCareService';

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

export default function ValueBasedCareList() {
  const navigate = useNavigate();
  const { token, permissions, decodedToken } = useAuth();
  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );

  const canList = permissionHas(userPermissionCodes, PermissionCodes.VALUE_BASED_CARE_LIST, decodedToken);
  const canViewDetails = permissionHas(userPermissionCodes, PermissionCodes.VALUE_BASED_CARE_VIEW, decodedToken);

  const [rows, setRows] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [summary, setSummary] = React.useState({ total_organizations: 0, total_providers: 0, total_facilities: 0 });

  const load = React.useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await getValueBasedCareOrganizations(token);
      if (res?.is_success && res.data) {
        setRows(Array.isArray(res.data.organizations) ? res.data.organizations : []);
        const s = res.data.summary || {};
        setSummary({
          total_organizations: num(s.total_organizations),
          total_providers: num(s.total_providers),
          total_facilities: num(s.total_facilities),
        });
      } else {
        setRows([]);
        setSummary({ total_organizations: 0, total_providers: 0, total_facilities: 0 });
        if (res?.message) toast.error(res.message);
      }
    } catch (error) {
      console.error('Error loading Value-Based Care organizations:', error);
      setRows([]);
      setSummary({ total_organizations: 0, total_providers: 0, total_facilities: 0 });
      toast.error('Unable to load Value-Based Care organizations.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    load();
  }, [load]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (row) =>
        (row.organization_name || '').toLowerCase().includes(term) ||
        (row.account_name || '').toLowerCase().includes(term),
    );
  }, [rows, search]);

  const stats = React.useMemo(
    () => [
      {
        label: 'Organizations',
        value: summary.total_organizations,
        icon: <MdBusiness size={22} color="var(--hc-icon-blue-text)" />,
        iconBg: 'var(--hc-icon-blue-bg)',
        accentClass: 'border-primary',
        valueClass: 'text-primary',
      },
      {
        label: 'Providers',
        value: summary.total_providers,
        icon: <MdHealing size={22} color="var(--hc-status-success-text)" />,
        iconBg: 'var(--hc-icon-green-bg)',
        accentClass: 'border-success',
        valueClass: 'text-success',
      },
      {
        label: 'Facilities',
        value: summary.total_facilities,
        icon: <MdLocationOn size={22} color="var(--hc-icon-amber-text)" />,
        iconBg: 'var(--hc-icon-amber-bg)',
        accentClass: 'border-warning',
        valueClass: 'text-warning',
      },
    ],
    [summary],
  );

  const goToView = React.useCallback(
    (row) => {
      if (!canViewDetails) return;
      navigate(APPRoutes.VALUE_BASED_CARE_VIEW.replace(':id', row.id), { state: { organization: row } });
    },
    [navigate, canViewDetails],
  );

  const columns = React.useMemo(
    () => [
      {
        id: 'organization_name',
        label: 'Organization',
        minWidth: 240,
        filterable: true,
        format: (value, row) =>
          canViewDetails ? (
            <button
              type="button"
              className="btn p-0 text-start text-dark border-0 bg-transparent"
              style={{ font: 'inherit' }}
              onClick={() => goToView(row)}
            >
              {value || '—'}
            </button>
          ) : (
            <span>{value || '—'}</span>
          ),
      },
      {
        id: 'account_name',
        label: 'Client',
        minWidth: 200,
        filterable: true,
        format: (value) => value || <span className="text-secondary">—</span>,
      },
      {
        id: 'facilities_count',
        label: 'Facilities',
        minWidth: 120,
        align: 'center',
        format: (value) => num(value),
      },
      {
        id: 'providers_count',
        label: 'Provider',
        minWidth: 120,
        align: 'center',
        format: (value) => num(value),
      },
      ...(canViewDetails ? [{
        id: 'actions',
        label: 'Action',
        minWidth: 110,
        align: 'center',
        format: (_value, row) => (
          <div className="d-flex align-items-center justify-content-center gap-2">
            <button
              type="button"
              title="View"
              className="btn btn-sm p-1 lh-1 text-primary border-0 rounded-2"
              style={{ backgroundColor: 'var(--hc-status-info-bg)' }}
              onClick={() => goToView(row)}
            >
              <MdVisibility size={16} />
            </button>
          </div>
        ),
      }] : []),
    ],
    [canViewDetails, goToView],
  );

  if (!canList) {
    return (
      <div className="container-fluid py-4">
        <PageTitle title="Value-Based Care | Aegis Health 360" />
        <div className="alert alert-danger">You do not have permission to view Value-Based Care.</div>
      </div>
    );
  }

  return (
    <div>
      <PageTitle title="Value-Based Care - Aegis Health 360" />
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3 mb-4">
        <div>
          <h4 className="fw-bold mb-1 hc-text-primary fs-3">Value-Based Care</h4>
          <p className="text-secondary mb-0 small">
            Select an organization to open its incentive-program patient group analytics.
          </p>
        </div>
      </div>

      <div className="row g-3 mb-3">
        {stats.map((stat) => (
          <div key={stat.label} className="col-12 col-sm-6 col-lg-4 col-xl">
            <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow p-3 d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
              <div className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`} />
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-3"
                style={{ width: 44, height: 44, backgroundColor: stat.iconBg }}
              >
                {stat.icon}
              </div>
              <div className="d-flex flex-column fw-bold lh-sm">
                <span className="text-dark" style={{ fontSize: '0.85rem' }}>
                  {stat.label}
                </span>
                <span className={`fw-bold ${stat.valueClass} fs-5`}>{stat.value}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-3 shadow-lg p-3 p-md-4">
        <div className="row g-2 mb-3 align-items-center">
          <div className="col-12">
            <div className="position-relative">
              <span
                className="position-absolute top-50 translate-middle-y d-inline-flex align-items-center justify-content-center text-secondary pe-none"
                style={{ left: '12px' }}
              >
                <MdSearch size={18} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm hc-input w-100"
                placeholder="Search by organization or client..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ paddingLeft: '36px', paddingRight: search ? '36px' : '12px', height: '40px' }}
              />
              {search ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                  className="btn btn-link bg-danger p-0 position-absolute top-50 translate-middle-y text-light rounded-circle"
                  style={{ right: '8px', lineHeight: 0 }}
                >
                  <MdClose size={18} />
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <DataGrid
          columns={columns}
          rows={filteredRows}
          loading={loading}
          skeletonRows={8}
          defaultRowsPerPage={10}
          stickyHeader
          getRowId={(row) => row.id ?? `${row.organization_name}-${row.account_name}`}
        />
      </div>
    </div>
  );
}
